/**
 * CoverArt — generated book-cover art. No real cover images exist in this
 * app (see StoryCover.jsx for the same approach in My Writing), so each
 * cover's `style` tag drives a genuinely different visual treatment —
 * borders, blobs, dividers, scale-contrast type — rather than a flat
 * gradient block, since the cover itself is meant to be the hero here.
 */

import React from 'react';
import { cx } from '@/components/site/primitives';

const TONE_HEX = {
  coral: { base: '#CC6F69', deep: '#7A3D38', light: '#F3D9D6', ink: '#3D211E' },
  yellow: { base: '#F0C77F', deep: '#8A6A29', light: '#FBEDD3', ink: '#4A3A14' },
  sage: { base: '#C5D7BD', deep: '#4F6249', light: '#E9F0E4', ink: '#2C3728' },
  blue: { base: '#8196A1', deep: '#3C4A52', light: '#DEE6E9', ink: '#22292D' },
};

function initials(title) {
  return title.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

const Grain = () => (
  <span
    aria-hidden
    className="pointer-events-none absolute inset-0 opacity-[0.4] mix-blend-multiply"
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.09'/%3E%3C/svg%3E\")",
    }}
  />
);

export default function CoverArt({ book, style = 'Illustrated', className = '' }) {
  const tone = TONE_HEX[book.tone] ?? TONE_HEX.coral;
  const letter = initials(book.title);

  if (style === 'Minimalist') {
    return (
      <div className={cx('relative flex flex-col overflow-hidden bg-[#FBF8F3] p-4', className)}>
        <span aria-hidden className="absolute inset-y-0 left-0 w-[5px]" style={{ background: tone.base }} />
        <span className="mt-2 font-display text-[3.5rem] font-bold leading-[0.85] tracking-tight" style={{ color: tone.base }}>
          {letter}
        </span>
        <div className="mt-auto">
          <span aria-hidden className="mb-2 block h-px w-8" style={{ background: tone.base }} />
          <p className="line-clamp-3 text-[11px] font-semibold uppercase leading-snug tracking-[0.12em] text-ink/75">{book.title}</p>
        </div>
        <Grain />
      </div>
    );
  }

  if (style === 'Dark Academia') {
    return (
      <div
        className={cx('relative flex items-end overflow-hidden p-3', className)}
        style={{ background: `radial-gradient(130% 110% at 25% 0%, ${tone.base}, ${tone.deep} 78%)` }}
      >
        <span aria-hidden className="pointer-events-none absolute inset-2 rounded-[2px] border border-white/25" />
        <span aria-hidden className="pointer-events-none absolute inset-[6px] rounded-[1px] border border-white/10" />
        <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent 60%)' }} />
        <div className="relative z-10 w-full text-center">
          <span aria-hidden className="mx-auto mb-1.5 block text-[10px] tracking-[0.3em] text-white/50">✦ ✦ ✦</span>
          <p className="font-display text-[15px] italic leading-snug text-white/95 text-balance">{book.title}</p>
        </div>
        <Grain />
      </div>
    );
  }

  if (style === 'Pastel') {
    return (
      <div
        className={cx('relative flex flex-col items-center justify-center gap-2.5 overflow-hidden p-4 text-center', className)}
        style={{ background: `linear-gradient(165deg, ${tone.light}, ${tone.base}55)` }}
      >
        <span aria-hidden className="pointer-events-none absolute -left-6 -top-8 h-28 w-28 rounded-full blur-xl" style={{ background: `${tone.base}66` }} />
        <span aria-hidden className="pointer-events-none absolute -bottom-10 -right-6 h-32 w-32 rounded-full blur-xl" style={{ background: `${tone.light}` }} />
        <span className="relative z-10 grid h-11 w-11 place-items-center rounded-full bg-white/80 font-display text-lg font-bold shadow-sm" style={{ color: tone.deep }}>
          {letter}
        </span>
        <p className="relative z-10 font-display text-[15px] font-semibold leading-snug text-ink/90 text-balance">{book.title}</p>
        <span aria-hidden className="relative z-10 font-hand text-lg" style={{ color: tone.deep }}>✿</span>
      </div>
    );
  }

  if (style === 'Typographic') {
    return (
      <div className={cx('relative flex flex-col justify-between overflow-hidden p-3.5', className)} style={{ background: tone.deep }}>
        <span aria-hidden className="pointer-events-none absolute -right-3 -top-5 font-display text-[7rem] font-bold leading-none text-white/[0.07]">{letter[0]}</span>
        <span aria-hidden className="h-px w-6 bg-white/40" />
        <p className="relative z-10 font-display text-[26px] font-bold uppercase leading-[0.92] tracking-tight text-white text-balance">{book.title}</p>
        <Grain />
      </div>
    );
  }

  if (style === 'Photographic') {
    return (
      <div
        className={cx('relative flex items-end overflow-hidden p-3', className)}
        style={{ background: `linear-gradient(200deg, ${tone.light} 0%, ${tone.base} 45%, ${tone.deep} 100%)` }}
      >
        <span aria-hidden className="pointer-events-none absolute right-3 top-3 h-3 w-3 rounded-full bg-white/50 ring-4 ring-white/10" />
        <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(90% 70% at 50% 100%, rgba(0,0,0,0.55), transparent 70%)' }} />
        <p className="relative z-10 font-display text-sm font-semibold leading-snug text-white/95 text-balance">{book.title}</p>
      </div>
    );
  }

  // Illustrated (default)
  return (
    <div className={cx('relative flex items-end overflow-hidden p-3', className)} style={{ background: `linear-gradient(155deg, ${tone.base}, ${tone.deep})` }}>
      <span aria-hidden className="pointer-events-none absolute -right-8 -top-10 h-40 w-40 rounded-full blur-2xl" style={{ background: `${tone.light}55` }} />
      <span aria-hidden className="pointer-events-none absolute -bottom-10 -right-6 font-display text-[6.5rem] font-bold leading-none text-white/[0.14]">{letter[0]}</span>
      <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-white/10" />
      <div className="relative z-10">
        <span aria-hidden className="mb-1 block h-[2px] w-5 rounded-full bg-white/70" />
        <p className="font-display text-[15px] font-semibold leading-snug text-white/95 text-balance">{book.title}</p>
      </div>
      <Grain />
    </div>
  );
}
